import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  ListItemButton,
  Collapse,
  Typography,
  Badge,
  Tooltip
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  ExpandLess,
  ExpandMore,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Event as EventIcon,
  Article as ArticleIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  SwapHoriz as SwapHorizIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Monitor as MonitorIcon,
  AssignmentInd as AssignmentIndIcon,
  Logout as LogoutIcon,
  History as HistoryIcon,
  Backpack as BackpackIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  LocalBar as LocalBarIcon,
  QrCode2 as QrCodeIcon,
  Language as WebIcon,
  Flight as FlightIcon,
  CardMembership as CardMembershipIcon,
  Groups as GroupsIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import TenantSwitcherModal from './TenantSwitcherModal';
import { getUserRequests } from '../services/userRequestService';
import { getPendingDocumentsCount } from '../services/documentService';
import UserAvatar from './UserAvatar';
import frontendLogger from '../utils/frontendLogger';

interface SideMenuProps {
  drawerOpen: boolean;
  toggleDrawer: (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => void;
  apiStatus: boolean;
  dbStatus: boolean;
}

// Local StatusIndicator component for SideMenu
const StatusIndicator: React.FC<{ status: boolean; label: string }> = ({ status, label }) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={`${label}: ${status ? t('connected') : t('disconnected')}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: status ? 'success.main' : 'error.main',
            boxShadow: status
              ? '0 0 6px rgba(77, 156, 126, 0.5)'
              : '0 0 6px rgba(231, 76, 60, 0.5)',
            transition: 'all 0.3s ease',
          }}
        />
        <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.7 }}>{label}</Typography>
      </Box>
    </Tooltip>
  );
};

const SideMenu: React.FC<SideMenuProps> = ({ drawerOpen, toggleDrawer, apiStatus, dbStatus }) => {
  const { t } = useTranslation();
  const { user, currentTenant, logout } = useAuth();
  const navigate = useNavigate();
  const [adminOpen, setAdminOpen] = React.useState(false);
  const [registryOpen, setRegistryOpen] = React.useState(false);
  const [tenantModalOpen, setTenantModalOpen] = React.useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingDocumentsCount, setPendingDocumentsCount] = useState(0);

  // Il ruolo va letto sul tenant CORRENTE, come fanno ProtectedRoute e il backend:
  // con .some() su tutti i tenant, chi e' admin altrove e semplice utente qui vedeva
  // una dozzina di voci che rimbalzavano tutte su /unauthorized.
  const isAdmin = currentTenant?.role === 'admin';
  const isOperator = currentTenant?.role === 'operator';
  const canManage = isAdmin || isOperator;
  // Il barista sta in cassa e basta: niente eventi, niente anagrafiche, solo il
  // bar. Il backend gli nega tutto il resto, il menu non deve nemmeno offrirlo.
  const isBarman = currentTenant?.role === 'barman';
  const vedeIlBar = (canManage || isBarman) && !!currentTenant?.barEnabled;
  // Chi salta ha i propri documenti, attrezzatura e brevetti; chi amministra
  // gestisce quelli degli altri dalla scheda persona. Istruttori compresi:
  // sono staff, ma saltano anche loro e i brevetti sono i loro.
  const mostraDatiPersonali = !canManage && !isBarman;

  // Chi gestisce la piattaforma amministra tutti i tenant; il titolare di una
  // scuola configura solo la propria. Sono due voci di menu diverse.
  const isPlatformAdmin = !!user?.isPlatformAdmin;

  const showTenantSwitcher = (user && user.tenants && user.tenants.length > 1) || isPlatformAdmin;

  const handleAdminClick = () => {
    setAdminOpen(!adminOpen);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    toggleDrawer(false)({} as any);
  };

  const handleTenantSwitchClick = () => {
    if (showTenantSwitcher) {
      setTenantModalOpen(true);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    const fetchPendingData = async () => {
      if (isAdmin || isOperator) {
        try {
          const requests = await getUserRequests('PENDING');
          setPendingRequestsCount(requests.length);

          const docCount = await getPendingDocumentsCount();
          setPendingDocumentsCount(docCount);
        } catch (error) {
          frontendLogger.error('Failed to fetch pending requests or documents:', error);
        }
      } else {
        setPendingRequestsCount(0);
        setPendingDocumentsCount(0);
      }
    };

    if (user && drawerOpen) {
      fetchPendingData();
    }
  }, [user, isAdmin, isOperator, drawerOpen]);

  const menuItemSx = {
    borderRadius: 2,
    mx: 1.5,
    mb: 0.5,
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(59, 192, 183, 0.08)',
    },
  };

  const activeIconColor = 'primary.main';

  const adminSubItemSx = {
    ...menuItemSx,
    pl: 4,
    ml: 3,
    mr: 1.5,
    borderLeft: '2px solid',
    borderColor: 'primary.main',
    borderRadius: '0 8px 8px 0',
  };

  return (
    /*
     * Il pulsante del menu ogni tanto smetteva di rispondere, senza nemmeno
     * fare l'effetto del tocco. Il colpevole e' lo sfondo scuro di questo
     * cassetto: le voci navigano e chiudono il cassetto nello stesso istante
     * (Link piu' toggleDrawer), e se il disegno della pagina nuova e' pesante
     * la fine dell'animazione di uscita puo' non arrivare mai. MUI allora non
     * marca la modale come nascosta, e lo sfondo resta li' — invisibile,
     * opacita' zero, ma ancora capace di intercettare i clic.
     *
     * Invece di rincorrere la transizione, si rende innocuo cio' che resta: se
     * lo stato dice che il cassetto e' chiuso, l'intera modale non riceve piu'
     * eventi, sfondo compreso. Quando e' aperto tutto funziona come prima.
     */
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={toggleDrawer(false)}
      sx={{ pointerEvents: drawerOpen ? 'auto' : 'none' }}
    >
      <Box sx={{ width: 280, display: 'flex', flexDirection: 'column', height: '100%' }} role="presentation">
        {/* Profile Section with gradient background */}
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #2C3E50 0%, #2a9d95 100%)',
            color: '#fff',
            transition: 'opacity 0.2s ease',
            '&:hover': { opacity: 0.95 },
          }}
          onClick={handleProfileClick}
        >
          <Box
            sx={{
              p: 0.4,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3BC0B7, #5dd4cc)',
              mb: 1.5,
            }}
          >
            <UserAvatar user={user} size={72} sx={{ border: '3px solid rgba(255,255,255,0.9)' }} />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem' }}>
              {user?.email}
            </Typography>
            {currentTenant && (
              <Box
                onClick={(e) => { e.stopPropagation(); handleTenantSwitchClick(); }}
                sx={{
                  cursor: showTenantSwitcher ? 'pointer' : 'default',
                  '&:hover': { opacity: showTenantSwitcher ? 0.8 : 1 },
                  mt: 0.5,
                }}
              >
                <Typography variant="subtitle2" display="block" sx={{ fontWeight: 700, color: '#fff' }}>
                  {currentTenant.name}
                  {showTenantSwitcher && (
                    <SwapHorizIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', opacity: 0.8 }} />
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <List sx={{ flexGrow: 1, pt: 1.5 }}>
          {!isBarman && (
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/events" onClick={toggleDrawer(false)} sx={menuItemSx}>
                <ListItemIcon sx={{ color: activeIconColor, minWidth: 40 }}>
                  <EventIcon />
                </ListItemIcon>
                <ListItemText primary={t('events')} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }} />
              </ListItemButton>
            </ListItem>
          )}

          {/* Dati personali di chi e' collegato. Nascosti a chi amministra:
              queste pagine, aperte dal menu, lavorano sul proprio profilo, e un
              admin che premeva "Aggiungi" si registrava addosso attrezzatura o
              brevetti credendo di fare l'anagrafica di qualcun altro. Lo staff
              gestisce le persone dalla loro scheda. */}
          {mostraDatiPersonali && (
            <>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/my-documents" onClick={toggleDrawer(false)} sx={menuItemSx}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <DescriptionIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('my_documents')} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }} />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton component={Link} to="/my-equipment" onClick={toggleDrawer(false)} sx={menuItemSx}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <BackpackIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('my_equipment', 'La Mia Attrezzatura')} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }} />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton component={Link} to="/my-qualifications" onClick={toggleDrawer(false)} sx={menuItemSx}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <WorkspacePremiumIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('my_qualifications', 'I Miei Brevetti')} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }} />
                </ListItemButton>
              </ListItem>
            </>
          )}

          {!isBarman && (
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/my-boarding-passes" onClick={toggleDrawer(false)} sx={menuItemSx}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <HistoryIcon />
                </ListItemIcon>
                <ListItemText primary={t('Flight History & Boarding Passes')} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }} />
              </ListItemButton>
            </ListItem>
          )}

          {vedeIlBar && (
            <>
              <Divider sx={{ my: 1, mx: 2, opacity: 0.5 }} />
              <ListItemButton component={Link} to="/admin/bar" onClick={toggleDrawer(false)} sx={menuItemSx}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LocalBarIcon />
                </ListItemIcon>
                <ListItemText primary={t('bar.title', 'Bar')} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }} />
              </ListItemButton>
            </>
          )}

          {canManage && (
            <>
              <Divider sx={{ my: 1, mx: 2, opacity: 0.5 }} />
              <ListItemButton component={Link} to="/admin/tickets" onClick={toggleDrawer(false)} sx={menuItemSx}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ConfirmationNumberIcon />
                </ListItemIcon>
                <ListItemText primary={t('tickets')} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }} />
              </ListItemButton>
              <ListItemButton onClick={handleAdminClick} sx={menuItemSx}>
                <ListItemIcon sx={{ color: activeIconColor, minWidth: 40 }}>
                  <AdminPanelSettingsIcon />
                </ListItemIcon>
                <ListItemText primary={t('administration')} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }} />
                {adminOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={adminOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton sx={adminSubItemSx} component={Link} to="/admin/users" onClick={toggleDrawer(false)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PeopleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('users')} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </ListItemButton>

                  <ListItemButton sx={adminSubItemSx} component={Link} to="/admin/requests" onClick={toggleDrawer(false)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Badge badgeContent={pendingRequestsCount} color="error">
                        <AssignmentIndIcon fontSize="small" />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText primary={t('adminRequests.menu_title')} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </ListItemButton>

                  <ListItemButton sx={adminSubItemSx} component={Link} to="/admin/approvals" onClick={toggleDrawer(false)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Badge badgeContent={pendingDocumentsCount} color="error">
                        <AssignmentIcon fontSize="small" />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText primary={t('documentApprovals.menu_title')} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </ListItemButton>

                  <ListItemButton sx={adminSubItemSx} component={Link} to="/admin/totems" onClick={toggleDrawer(false)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <MonitorIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('totems')} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </ListItemButton>

                  <ListItemButton sx={adminSubItemSx} component={Link} to="/admin/registration-qr" onClick={toggleDrawer(false)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <QrCodeIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('registrationQr.menu', 'QR registrazione')} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </ListItemButton>

                  <ListItemButton sx={adminSubItemSx} component={Link} to="/admin/website-widget" onClick={toggleDrawer(false)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <WebIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('widget.menu', 'Widget per il sito')} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </ListItemButton>
                  {!isPlatformAdmin && canManage && (
                    <ListItemButton sx={adminSubItemSx} component={Link} to="/admin/my-tenant" onClick={toggleDrawer(false)}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <BusinessIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={t('settings')} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                    </ListItemButton>
                  )}
                  {isPlatformAdmin && (
                    <>
                      <ListItemButton sx={adminSubItemSx} component={Link} to="/admin/tenants" onClick={toggleDrawer(false)}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <BusinessIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={t('tenants')} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                      </ListItemButton>
                      <ListItemButton sx={adminSubItemSx} component={Link} to="/admin/logs" onClick={toggleDrawer(false)}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <MonitorIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={t('adminLogs.menuTitle')} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                      </ListItemButton>
                    </>
                  )}
                </List>
              </Collapse>
              <ListItemButton onClick={() => setRegistryOpen(!registryOpen)}>
                <ListItemIcon>
                  <InventoryIcon />
                </ListItemIcon>
                <ListItemText primary={t('registries', 'Registries')} />
                {registryOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={registryOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton sx={{ pl: 4 }} component={Link} to="/admin/document-types" onClick={toggleDrawer(false)}>
                    <ListItemIcon>
                      <ArticleIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('document_types')} />
                  </ListItemButton>
                  <ListItemButton sx={{ pl: 4 }} component={Link} to="/admin/aircrafts" onClick={toggleDrawer(false)}>
                    <ListItemIcon>
                      <FlightIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('aircrafts')} />
                  </ListItemButton>
                  <ListItemButton sx={{ pl: 4 }} component={Link} to="/admin/qualifications" onClick={toggleDrawer(false)}>
                    <ListItemIcon>
                      <CardMembershipIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('qualifications.title', 'Qualifications')} />
                  </ListItemButton>
                  <ListItemButton sx={{ pl: 4 }} component={Link} to="/admin/jump-group-roles" onClick={toggleDrawer(false)}>
                    <ListItemIcon>
                      <GroupsIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('jumpGroupRoles.title', 'Jump Group Roles')} />
                  </ListItemButton>
                </List>
              </Collapse>
            </>
          )}

          <Divider sx={{ my: 1, mx: 2, opacity: 0.5 }} />

          {/* Logout Button */}
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout} sx={{ ...menuItemSx, '&:hover': { backgroundColor: 'rgba(231, 76, 60, 0.08)' } }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon color="error" />
              </ListItemIcon>
              <ListItemText primary={t('logout')} primaryTypographyProps={{ color: 'error', fontWeight: 500, fontSize: '0.9rem' }} />
            </ListItemButton>
          </ListItem>

        </List>
        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', gap: 3, borderTop: 1, borderColor: 'divider' }}>
          <StatusIndicator status={apiStatus} label={t('api_label')} />
          <StatusIndicator status={dbStatus} label={t('db_label')} />
        </Box>
      </Box>
      <TenantSwitcherModal open={tenantModalOpen} onClose={() => setTenantModalOpen(false)} />
    </Drawer>
  );
};

export default SideMenu;
